import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import helper from '@/constants/helper';
import { extractBackendCode } from '@/lib/api/extractBackendCode';
import { disconnectSocket } from '@/utils/socket';
import { saveLastVisitedPage } from '../utils/saveLastVisitedPage';
// import {clearCookies} from "../Constants/clearCookies";

/**
 * The one 403 that ends no session (#1207): `checkImportPermission`'s refusal of a data-analysis
 * import write, `403 {message, code: 'importPermissionDenied'}` (`IMPORT_PERMISSION_DENIED_CODE`
 * in `packages/api/services/auth/auditAccess.js`). It refuses an action, not the caller, and the
 * import wizard words it inline. Keyed to that code alone: every other 401 and 403 still logs
 * out (DATA_FLOW.md §1).
 */
const isImportPermissionRefusal = (error: AxiosError): boolean =>
  error.response?.status === 403 && extractBackendCode(error) === 'importPermissionDenied';

/**
 * Builds one axios instance.
 *
 * `token === null` means the SHARED instance: it is built once and outlives login,
 * `X-New-Token` rotation and logout, so it must read the stored JWT per REQUEST rather
 * than baking a header in at construction — otherwise the session would be pinned to
 * whatever token existed the first time anything called {@link API}.
 *
 * A non-null token means a throwaway instance with the header baked in, exactly as
 * before. Those callers (password reset, SMS verification in `api/user/user.ts`) run
 * pre-login, where the stored JWT is absent or belongs to a different session.
 */
function createInstance(token: string | null): AxiosInstance {
  const baseURL = process.env.REACT_APP_API_URL;
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  if (token) {
    headers.Authorization = 'Bearer ' + token;
  }

  const api = axios.create({
    baseURL: baseURL,
    timeout: 600000,
    headers: headers,
  });

  api.interceptors.request.use((request) => requestHandler(request));

  api.interceptors.response.use(
    (response) => successHandler(response),
    (error) => errorHandler(error),
  );

  const successHandler = (response: AxiosResponse): AxiosResponse => {
    // Rotation extends a live session and never starts one: a response that lands after a
    // logout removed the token belongs to the ended session, and writing it back would
    // resurrect it. No sign-in relies on the header (DATA_FLOW.md §1 "Token rotation").
    if (response.headers['x-new-token'] && localStorage.getItem(helper.JWT_TOKEN)) {
      localStorage.setItem(helper.JWT_TOKEN, response.headers['x-new-token']);
    }
    return response;
  };
  const requestHandler = (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // Baked-token instances keep their construction-time header untouched.
    if (token === null) {
      const stored = localStorage.getItem(helper.JWT_TOKEN);
      // Deleting on absence matters as much as setting: after `forceLogout` the shared
      // instance must stop sending the cleared token.
      if (stored) {
        request.headers.set('Authorization', 'Bearer ' + stored);
      } else {
        request.headers.delete('Authorization');
      }
    }
    return request;
  };

  const errorHandler = (error: AxiosError): Promise<never> => {
    if (
      (error?.response?.status === 401 || error?.response?.status === 403) &&
      !isImportPermissionRefusal(error)
    ) {
      if (error.response.data === 'Token expired') {
        saveLastVisitedPage();
      }
      userLogout();
      return Promise.reject({ ...error });
    } else if (error?.response?.status === 404) {
      console.error(error);
      // debugger
      // window.location = "/404"
      return Promise.reject({ ...error });
    } else {
      // A request its caller aborted (a superseded read, an unmounted query) is no
      // failure, so it is not logged as one (#510).
      if (!axios.isCancel(error)) console.error(error);
      // debugger
      return Promise.reject({ ...error });
    }
  };
  return api;
}

/** Built on first use, then reused — see {@link API}. */
let sharedInstance: AxiosInstance | null = null;

/**
 * The app's HTTP client.
 *
 * The no-arg form returns ONE memoised instance. It used to construct a fresh axios
 * instance — and register two interceptors — on every one of ~300 call sites, which put
 * `axios.create`'s config merge on the critical path of every user interaction.
 */
function API(token: string | null = null): AxiosInstance {
  if (token !== null) {
    return createInstance(token);
  }
  sharedInstance ??= createInstance(null);
  return sharedInstance;
}

export function userLogout(isUser: boolean | null = null): void {
  if (isUser) {
    API()
      .post('/user/logout')
      .then(() => forceLogout())
      .catch(() => forceLogout());
  } else {
    forceLogout();
  }
}

export function forceLogout(): void {
  disconnectSocket();
  localStorage.removeItem(helper.JWT_TOKEN);
  localStorage.removeItem(helper.BEXIO_REDIRECT_URL);
  localStorage.removeItem(helper.BEXIO_REQUEST_DATA);
  localStorage.removeItem(helper.BEXIO_TOKEN);

  sessionStorage.clear();

  window.location.href = '/login';
}

/**
 * The public-document plane (BE-011).
 *
 * `POST /public/document/:id/:accessCode/verify` mints a token carrying
 * `scope: "public-document"` and a `documentId` claim.
 * `packages/api/services/auth/resolveSessionToken.js` refuses a scoped token by default;
 * `packages/api/middleware/attachGuestUser.js` is the single caller that opts in. So this
 * token opens the /public/document/* routes and nothing else, and storing it under the
 * SHARED session key broke the other direction: a signed-in auditor who opened a public
 * link had their portal JWT overwritten, 403'd on their next portal request, and was
 * force-logged-out by {@link errorHandler}.
 *
 * Hence its own key and its own instance. Three differences from the shared plane:
 *
 * - it sends the PUBLIC token and never the portal one, and sends NO `Authorization` when
 *   there is none — a `public` document is readable by an anonymous caller, so an absent
 *   token is a supported state rather than an error;
 * - an `x-new-token` header is written to the PUBLIC key. The guest routes do not rotate
 *   today (no `attachCurrentUser` in their stacks), but the shared `successHandler` would
 *   have laundered any header they ever emitted into a portal session — the exact
 *   escalation BE-011 closed on the server;
 * - a 401/403 does NOT reach {@link forceLogout}. It drops the stale public token so the
 *   caller falls back to the access form, and never disconnects the socket, clears
 *   sessionStorage or redirects to /login: this plane's failures must not end a portal
 *   session, and a guest has no /login to be bounced to.
 *
 * `attachGuestUser` answers 404 for every refusal it makes; the one 401 today is
 * `addPublicComment`'s to an anonymous caller on a public document.
 */
function createPublicDocumentInstance(): AxiosInstance {
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    timeout: 600000,
    headers: {
      Accept: 'application/json',
    },
  });

  api.interceptors.request.use((request: InternalAxiosRequestConfig) => {
    const stored = localStorage.getItem(helper.PUBLIC_DOCUMENT_TOKEN);
    if (stored) {
      request.headers.set('Authorization', 'Bearer ' + stored);
    } else {
      request.headers.delete('Authorization');
    }
    return request;
  });

  api.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => {
      if (response.headers['x-new-token']) {
        localStorage.setItem(helper.PUBLIC_DOCUMENT_TOKEN, response.headers['x-new-token']);
      }
      return response;
    },
    (error: AxiosError): Promise<never> => {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        clearPublicDocumentToken();
      } else {
        console.error(error);
      }
      return Promise.reject({ ...error });
    },
  );

  return api;
}

/** Built on first use, then reused — mirrors the shared instance. */
let publicDocumentInstance: AxiosInstance | null = null;

/** The HTTP client for the /public/document/* routes. See {@link createPublicDocumentInstance}. */
export function publicDocumentAPI(): AxiosInstance {
  publicDocumentInstance ??= createPublicDocumentInstance();
  return publicDocumentInstance;
}

/** Store the token returned by the SMS gate. The only writer of the public key. */
export function setPublicDocumentToken(token: string): void {
  localStorage.setItem(helper.PUBLIC_DOCUMENT_TOKEN, token);
}

/** Drop the public token without touching the portal session. */
export function clearPublicDocumentToken(): void {
  localStorage.removeItem(helper.PUBLIC_DOCUMENT_TOKEN);
}

export default API;
